import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import axios from 'axios';
import CenterModalWindow from '../layout/ReusableComponent/CenterModalWindow/CenterModalWindow';
import { APP_NAME, LOGO_SRC } from '../config/branding';
import './signin.css';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_CLOUDFARE_TURNSTILE_CLIENT_SITE_KEY || '';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const turnstileRequired = Boolean(TURNSTILE_SITE_KEY);

  useEffect(() => {
    document.body.classList.add('signin-page-active');
    return () => document.body.classList.remove('signin-page-active');
  }, []);

  useEffect(() => {
    const syncTheme = () => {
      setIsDarkTheme(document.documentElement.getAttribute('data-theme') === 'dark');
    };
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const resetTurnstile = () => {
    setTurnstileToken('');
    turnstileRef.current?.reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (password !== confirmPassword) {
      setError('Passwords do not match. Please retype your password.');
      return;
    }

    if (turnstileRequired && !turnstileToken) {
      setError('Please complete the security verification.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${baseUrl}/api/reset-password`,
        { token, password, confirmPassword, turnstileToken },
        { withCredentials: true }
      );

      if (response.data.success) {
        setSuccessMessage(response.data.message || 'Password updated successfully.');
      } else {
        setError(response.data.message || 'Unable to reset password.');
        resetTurnstile();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to reset password. The link may have expired.');
      resetTurnstile();
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalOpen = Boolean(token) && !successMessage;

  return (
    <div className="signin-page signin-page--loading">
      <header className="signin-topbar">
        <button type="button" className="signin-logo-btn" onClick={() => navigate('/')} aria-label="Go to home">
          <img src={LOGO_SRC} alt={APP_NAME} className="signin-logo-img" />
        </button>
      </header>

      <CenterModalWindow
        isOpen={modalOpen}
        onClose={() => navigate('/signin')}
        width="min(440px, 94vw)"
        height="auto"
        contentClassName="signin-auth-modal"
      >
        <div className="signin-auth-modal-body">
          <img src={LOGO_SRC} alt="" className="signin-auth-modal-logo" aria-hidden="true" />
          <p className="signin-auth-modal-eyebrow">{APP_NAME}</p>
          <h2 className="signin-auth-modal-title">Choose a new password</h2>
          <p className="signin-auth-modal-text">
            Enter and confirm your new password below.
          </p>

          {!token ? (
            <div className="signin-auth-modal-message signin-auth-modal-message--error" role="alert">
              Reset link is invalid or missing.
            </div>
          ) : (
            <form className="signin-auth-modal-form" onSubmit={handleSubmit}>
              <label className="signin-field">
                <span className="signin-field-label">New password</span>
                <input
                  type="password"
                  className="signin-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={isSubmitting}
                  minLength={8}
                />
              </label>
              <label className="signin-field">
                <span className="signin-field-label">Retype new password</span>
                <input
                  type="password"
                  className="signin-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={isSubmitting}
                  minLength={8}
                />
              </label>

              {turnstileRequired ? (
                <div className="signin-turnstile">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={setTurnstileToken}
                    onExpire={() => setTurnstileToken('')}
                    options={{ theme: isDarkTheme ? 'dark' : 'light' }}
                  />
                </div>
              ) : null}

              {error ? (
                <div className="signin-auth-modal-message signin-auth-modal-message--error" role="alert">
                  {error}
                </div>
              ) : null}

              <button type="submit" className="signin-submit signin-auth-modal-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving…' : 'Save new password'}
              </button>
            </form>
          )}
        </div>
      </CenterModalWindow>

      <CenterModalWindow
        isOpen={Boolean(successMessage)}
        onClose={() => navigate('/signin')}
        width="min(440px, 94vw)"
        height="auto"
        contentClassName="signin-auth-modal"
      >
        <div className="signin-auth-modal-body">
          <img src={LOGO_SRC} alt="" className="signin-auth-modal-logo" aria-hidden="true" />
          <p className="signin-auth-modal-eyebrow">{APP_NAME}</p>
          <h2 className="signin-auth-modal-title">Password updated</h2>
          <div className="signin-auth-modal-message signin-auth-modal-message--success" role="status">
            {successMessage}
          </div>
          <button
            type="button"
            className="signin-submit signin-auth-modal-submit"
            onClick={() => navigate('/signin')}
          >
            Go to sign in
          </button>
        </div>
      </CenterModalWindow>

      {!token && !successMessage ? (
        <main className="signin-verify-email">
          <p className="signin-join">
            <Link to="/signin" className="signin-link">Back to sign in</Link>
          </p>
        </main>
      ) : null}
    </div>
  );
}

export default ResetPassword;
